import { orderedDaysOfWeek, DayOfWeek } from '@caresend/types';
import { getDistanceBetweenLocations, formatOpeningHours } from '@caresend/utils';
import { whiteDot } from '../../../../assets/icons/WhiteDot.js';
import { getStore } from '../../../../store/helpers/initStore.js';

/**
 * Adjust the opening hours for friday if needed - controlled by the `fridayBrickAndMortarCutoffTime` variable
 * if friday's opening hours start after the cutoff time, filter out friday
 *
 * @param openingHours - The opening hours to adjust. Assumed to not have any 'overflowing' day ranges,
 * for example, no instances of Friday - Monday. Logic must be adjusted if this is not the case.
 * @returns The adjusted opening hours
 */
const maybeAdjustFridayOpeningHours = (openingHours) => {
    const fridayCutoffTime = getStore().getters['variables/getFridayBrickAndMortarCutoffTime'];
    const fridayIndex = 4;
    const adjustedOpeningHours = [];
    openingHours.forEach((openingHour) => {
        const dayFromIndex = orderedDaysOfWeek.indexOf(openingHour.dayFrom);
        const dayToIndex = orderedDaysOfWeek.indexOf(openingHour.dayTo);
        // Do not adjust if friday isn't in range,
        // if the opening hours are before the cutoff time anyways,
        // or if the opening hours are deliberately after the cutoff time
        if (fridayIndex < dayFromIndex || fridayIndex > dayToIndex
            || Number(openingHour.timeTo) < fridayCutoffTime) {
            adjustedOpeningHours.push(openingHour);
            return;
        }
        const openingHoursAreAfterCutoff = Number(openingHour.timeFrom) >= fridayCutoffTime;
        const fridayHours = {
            dayFrom: DayOfWeek.FRIDAY,
            dayTo: DayOfWeek.FRIDAY,
            timeFrom: openingHour.timeFrom,
            timeTo: String(fridayCutoffTime),
        };
        // If the opening hours are for friday, adjust the time to the cutoff time just for this day
        if (dayFromIndex === fridayIndex && dayToIndex === fridayIndex) {
            // If the opening hours are for friday start after our cutoff, filter out friday
            if (openingHoursAreAfterCutoff)
                return;
            adjustedOpeningHours.push(fridayHours);
            return;
        }
        // If the start or end day is friday, adjust the respective day to the next closest day
        // (thursday or saturday respectively), and add friday hours after or before respectively
        if (dayFromIndex === fridayIndex || dayToIndex === fridayIndex) {
            const dayFrom = dayFromIndex === fridayIndex ? DayOfWeek.SATURDAY : openingHour.dayFrom;
            const dayTo = dayToIndex === fridayIndex ? DayOfWeek.THURSDAY : openingHour.dayTo;
            const adjustedDays = { ...openingHour, dayFrom, dayTo };
            // If the opening hours for friday start after our cutoff, filter out friday
            if (openingHoursAreAfterCutoff) {
                adjustedOpeningHours.push(adjustedDays);
                return;
            }
            const pushOrder = dayFrom === DayOfWeek.SATURDAY ? [fridayHours, adjustedDays] : [adjustedDays, fridayHours];
            adjustedOpeningHours.push(...pushOrder);
            return;
        }
        // If the opening hours have friday in BETWEEN the range, add the hours before friday with regular close
        // time, then friday with the cutoff time (unless it's after the cutoff, then filter it out),
        // then the hours after friday with regular close time
        adjustedOpeningHours.push({ ...openingHour, dayTo: DayOfWeek.THURSDAY });
        if (openingHoursAreAfterCutoff)
            adjustedOpeningHours.push(fridayHours);
        adjustedOpeningHours.push({ ...openingHour, dayFrom: DayOfWeek.SATURDAY });
    });
    return adjustedOpeningHours;
};
const getBrickAndMortarLab = (place, placeGroupName, placePhoneNumber, draftWaypointLocation) => {
    var _a;
    let distanceToAddress = 0;
    if (place.location && draftWaypointLocation) {
        distanceToAddress = getDistanceBetweenLocations(place.location, draftWaypointLocation, true);
    }
    const openingHoursWithMaybeAdjustedFridays = maybeAdjustFridayOpeningHours((_a = place.openingHours) !== null && _a !== void 0 ? _a : []);
    const appointmentOnly = 'appointmentOnly' in place
        ? place.appointmentOnly
        : false;
    return {
        ...(appointmentOnly && { appointmentOnly }),
        distanceToAddress,
        location: place.location,
        name: placeGroupName,
        openingHours: formatOpeningHours(openingHoursWithMaybeAdjustedFridays !== null && openingHoursWithMaybeAdjustedFridays !== void 0 ? openingHoursWithMaybeAdjustedFridays : []),
        phoneNumber: placePhoneNumber,
        placeGroupID: place.placeGroupID,
        placeID: place.id,
        price: 0,
    };
};
const convertOpeningHoursToDaysOfWeek = (openingHours) => {
    const set = new Set();
    const days = [
        DayOfWeek.SUNDAY,
        DayOfWeek.MONDAY,
        DayOfWeek.TUESDAY,
        DayOfWeek.WEDNESDAY,
        DayOfWeek.THURSDAY,
        DayOfWeek.FRIDAY,
        DayOfWeek.SATURDAY,
    ];
    openingHours.forEach((openingHour) => {
        const startIndex = days.indexOf(openingHour.dayFrom);
        const endIndex = days.indexOf(openingHour.dayTo);
        if (set.has(endIndex))
            return;
        days.forEach((_, index) => {
            if (index >= startIndex && index <= endIndex) {
                set.add(index);
            }
        });
    });
    set.delete(-1);
    return Array.from(set);
};
const getBrickAndMortarLabs = (placeIDs) => {
    var _a;
    const store = getStore();
    const { places = {}, placeGroups = {} } = store.state.places;
    const draftWaypointLocation = (_a = store.state.schedulingFlow.draftWaypoint) === null || _a === void 0 ? void 0 : _a.location;
    return placeIDs.reduce((labInfos, placeID) => {
        const place = places === null || places === void 0 ? void 0 : places[placeID];
        if (!place)
            return labInfos;
        const placeGroup = placeGroups === null || placeGroups === void 0 ? void 0 : placeGroups[place.placeGroupID];
        if (!placeGroup)
            return labInfos;
        const placePhoneNumber = place.contactNumber;
        const placeGroupName = placeGroup.name;
        const labInfo = getBrickAndMortarLab(place, placeGroupName, placePhoneNumber, draftWaypointLocation);
        labInfos.push(labInfo);
        return labInfos;
    }, []).sort((a, b) => a.distanceToAddress - b.distanceToAddress);
};
const getBrickAndMortarMapSource = (latLng, id, markerStyle, price) => {
    const point = {
        type: 'Point',
        coordinates: [latLng.lng, latLng.lat],
    };
    const marker = {
        markerStyle,
        isClickable: markerStyle === 'price',
        price,
    };
    const feature = {
        type: 'Feature',
        geometry: point,
        properties: { id, marker, highlight: true },
    };
    const mapSource = {
        id,
        iconSize: 0.2,
        iconUrl: whiteDot,
        data: [feature],
    };
    return mapSource;
};
const getBrickAndMortarMapLabSource = (labInfo) => getBrickAndMortarMapSource({
    lat: labInfo.location.lat,
    lng: labInfo.location.lng,
}, labInfo.placeID, 'price');
const getBrickAndMortarMapYouSource = (location) => getBrickAndMortarMapSource({
    lat: location.lat,
    lng: location.lng,
}, 'selectedVisitLocation', 'you', 0);

export { convertOpeningHoursToDaysOfWeek, getBrickAndMortarLab, getBrickAndMortarLabs, getBrickAndMortarMapLabSource, getBrickAndMortarMapYouSource, maybeAdjustFridayOpeningHours };
